// =====================================================
// ðŸ–¼ï¸ DASHBOARD LAYOUT - MENÚS ACTUALIZADOS POR ROL
// =====================================================

import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { useI18n } from '../../hooks/useI18n'
import { useTheme } from '../../contexts/ThemeContext'
import { 
  Search, Bell, Settings, Menu, X,
  MessageCircle, Home, FileText, HelpCircle,
  Users, Sun, Moon, Globe, ChevronDown, Ticket, Cog, LogOut,
  ChevronRight, ChevronLeft, PanelLeftClose, PanelLeftOpen,
  LayoutDashboard, Plus, Crown, CreditCard, Package, Activity   
} from 'lucide-react'
import iconlogo from '../../assets/images/favicon.png'
import { useNotifications } from '../../hooks/useNotifications';
import { useToast } from '../../hooks/useToast';

interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  timestamp: Date
}

export default function DashboardLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, clearAuth } = useAuthStore()
  const { language, changeLanguage } = useI18n()
  const { toggleTheme, isDark } = useTheme()
  
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [termsModalOpen, setTermsModalOpen] = useState(false)
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false)
  const { success } = useToast()
  
  // âœ… DEBUG: Verificar el rol del usuario
  useEffect(() => {
    console.log('ðŸ” [DEBUG] Usuario actual:', {
      hasUser: !!user,
      userId: user?.id,
      userName: user?.name,
      userRole: user?.role,
      roleType: typeof user?.role
    })
  }, [user])
  
  
   // ✅ FUNCIÓN PARA VERIFICAR ACCESO A BÚSQUEDA NP
 const hasPartsSearchAccess = () => {
    if (!user) return false;
    
    console.log('🔍 [PARTS ACCESS CHECK]', {
      role: user.role,
      subscription_type: user.subscription_type,
      is_premium: user.is_premium
    });
    
    // 1. Admin siempre tiene acceso
   if (user.role === 'admin' || user.role === 'technician') {
      console.log('✅ Acceso por ROL:', user.role);
      return true;
    }
    
    // 2. Plan premium (por subscription_type)
    if (user.subscription_type === 'premium') {
      console.log('✅ Acceso por PLAN: premium');
      return true;
    }
    
    // 3. Plan professional
    if (user.subscription_type === 'professional') {
      console.log('✅ Acceso por PLAN: professional');
      return true;
    }
    
    // 4. Flag is_premium
    if (user.is_premium === true) {
      console.log('✅ Acceso por FLAG: is_premium');
      return true;
    }
    
    console.log('❌ Sin acceso a búsqueda NP');
    return false;
  };


 // ✅ MENÚS DINÁMICOS
  const getBaseMenuItems = () => {
    const baseMenus = {
      admin: [
        { icon: Home, label: 'Dashboard', path: '/dashboard' },
        { icon: LayoutDashboard, label: 'Panel Admin', path: '/dashboard/admin' }, 
        { icon: MessageCircle, label: 'Asistente Técnico IA', path: '/dashboard/chat' },
        { icon: Ticket, label: 'Tickets', path: '/dashboard/tickets' },
        { icon: Activity, label: 'Monitoreo', path: '/dashboard/monitoring' },
        { icon: FileText, label: 'Documentación', path: '/dashboard/docs' },
        { icon: HelpCircle, label: 'FAQs', path: '/dashboard/faqs' },
        { icon: Users, label: 'Usuarios', path: '/dashboard/users' },
        { icon: CreditCard, label: 'Pagos', path: '/dashboard/payments' },
        { icon: Crown, label: 'Planes', path: '/dashboard/plans' },
        { icon: Settings, label: 'Configuración', path: '/dashboard/settings' }
      ],
      technician: [
        { icon: Home, label: 'Dashboard', path: '/dashboard' },
        { icon: MessageCircle, label: 'Asistente Técnico IA', path: '/dashboard/chat' },
        { icon: Ticket, label: 'Tickets', path: '/dashboard/tickets' },
        { icon: Activity, label: 'Monitoreo', path: '/dashboard/monitoring' },
        { icon: FileText, label: 'Documentación', path: '/dashboard/docs' },
        { icon: HelpCircle, label: 'FAQs', path: '/dashboard/faqs' },
        { icon: Settings, label: 'Configuración', path: '/dashboard/settings' }
      ],
      user: [
        { icon: Home, label: 'Dashboard', path: '/dashboard' },
        { icon: MessageCircle, label: 'Asistente Técnico IA', path: '/dashboard/chat' },
        { icon: Ticket, label: 'Tickets', path: '/dashboard/tickets' },
        { icon: FileText, label: 'Documentación', path: '/dashboard/docs' },
        { icon: HelpCircle, label: 'FAQs', path: '/dashboard/faqs' },
        { icon: Settings, label: 'Configuración', path: '/dashboard/settings' }
      ]
    };

     const normalizedRole = user?.role?.toString().toLowerCase().trim() || 'user';
    
    const roleMapping: { [key: string]: string } = {
      'admin': 'admin',
      'administrator': 'admin',
      'administrador': 'admin',
      'technician': 'technician',
      'tecnico': 'technician',
      'técnico': 'technician',
      'user': 'user',
      'usuario': 'user',
      'agent': 'agent',
      'agente': 'agent'
    };

    const mappedRole = roleMapping[normalizedRole] || 'user';
    return baseMenus[mappedRole as keyof typeof baseMenus] || baseMenus.user;
  };

  const getUserMenu = () => {
    const menu = [...getBaseMenuItems()]; // Clonar array
    
    // Si tiene acceso, insertar después de Chat
    if (hasPartsSearchAccess()) {
      const partsItem = { 
        icon: Package, 
        label: 'Búsqueda NP', 
        path: '/dashboard/busqueda-np' 
      };
      
      const chatIndex = menu.findIndex(item => item.path === '/dashboard/chat');
      if (chatIndex !== -1) {
        menu.splice(chatIndex + 1, 0, partsItem);
      } else {
        // Si no hay chat, agregar después de Dashboard
        menu.splice(1, 0, partsItem);
      }
    }
    
    return menu;
  };

  const menuItems = getUserMenu();
    
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'Nuevo ticket asignado',
      message: 'Se te ha asignado el ticket #1234',
      type: 'info',
      read: false,
      timestamp: new Date()
    },
    {
      id: '2',
      title: 'Actualización del sistema',
      message: 'Nueva versión disponible',
      type: 'success',
      read: false,
      timestamp: new Date()
    }
  ])

  const unreadCount = notifications.filter(n => !n.read).length
 

  const markNotificationAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    )
  }

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

const handleLogout = async () => {
  await clearAuth();
  navigate('/login');
};

  const languages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ]

  return (
    <div className={`h-screen flex ${isDark ? 'dark' : ''}`}>
      
      {/* ============================================= */}
      {/* SIDEBAR */}
      {/* ============================================= */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50  ${
        sidebarExpanded ? 'w-64' : 'w-20'
      } bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <img src={iconlogo} alt="Logo" className="w-10 h-10" />
            {sidebarExpanded && (
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                KIKAI
              </span>
            )}
          </div>
        </div>

        {/* Toggle button */}
        <div className="flex justify-end p-2">
          <button
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            className="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400"
          >
            {sidebarExpanded ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto">
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path
            return (
              <button
                key={index}
                onClick={() => {
                  navigate(item.path)
                  setMobileMenuOpen(false)
                }}
                className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                } ${!sidebarExpanded && 'justify-center'}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarExpanded && <span className="font-medium text-sm">{item.label}</span>}
              </button>
            )
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-950">
        {/* HEADER */}
        <header className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-700 dark:text-gray-300"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
             {/* Manual Download Button */}
           <button
             onClick={async () => {
               try {
                 success('Descargando manual de usuario...');
                 
                 const token = localStorage.getItem('kikai_token');
                 if (!token) {
                   console.error('No hay token de autenticación');
                   return;
                 }
           
                 const response = await fetch('/api/documents/manual', {
                   method: 'GET',
                   headers: {
                     'Authorization': `Bearer ${token}`
                   }
                 });
           
                 if (!response.ok) {
                   throw new Error(`Error: ${response.status}`);
                 }
           
                 const blob = await response.blob();
                 const url = window.URL.createObjectURL(blob);
                 const link = document.createElement('a');
                 link.href = url;
                 link.download = 'Manual_Usuario_KIKAI.pdf';
                 document.body.appendChild(link);
                 link.click();
                 document.body.removeChild(link);
                 window.URL.revokeObjectURL(url);
                 
               } catch (error) {
                 console.error('Error al descargar manual:', error);
               }
             }}
             className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-700 dark:text-red-600"
             title="Descargar Manual de Usuario"
           >
             <HelpCircle className="w-5 h-5" />
           </button>

            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg flex items-center space-x-1 text-gray-700 dark:text-gray-300 transition-colors"
              >
                <Globe className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:block">
                  {languages.find(l => l.code === language)?.flag || 'ðŸ‡ªðŸ‡¸'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {langMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={async () => {
                          await changeLanguage(lang.code)
                          setLangMenuOpen(false)
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 transition-colors ${
                          language === lang.code 
                            ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-700 dark:text-gray-300 relative"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-gray-900"></span>
                )}
              </button>

              {notificationsOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotificationsOpen(false)} />
                  <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                      <h3 className="font-semibold text-gray-900 dark:text-white">Notificaciones</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Marcar todas como leídas
                        </button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.map(notif => (
                        <div
                          key={notif.id}
                          onClick={() => markNotificationAsRead(notif.id)}
                          className={`p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${
                            !notif.read ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                              {notif.title}
                            </h4>
                            {!notif.read && (
                              <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{notif.message}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {notif.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="relative pl-3 border-l border-gray-200 dark:border-gray-700 hidden lg:block">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="text-left hidden xl:block">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name || 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email || 'user@example.com'}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 hidden xl:block" />
              </button>

              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user?.name || 'Usuario'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user?.email || 'user@example.com'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/dashboard/settings')
                        setUserMenuOpen(false)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Mi cuenta</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Cerrar sesión</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* CONTENIDO */}
        <main className="flex-1 overflow-auto p-4 sm:p-6">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>

        {/* FOOTER */}
        <footer className="h-8 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto h-full flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Copyright © 2026 <span className="font-semibold text-gray-900 dark:text-white">KIKAI</span>. Todos los derechos reservados.
            </span>
            <div className="hidden sm:flex items-center space-x-4 text-sm">
              <button 
                onClick={() => setTermsModalOpen(true)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Términos & Condiciones
              </button>
              <span className="text-gray-400">|</span>
              <button 
                onClick={() => setPrivacyModalOpen(true)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Política de Privacidad
              </button>
            </div>
          </div>
        </footer>

        {/* ============================================= */}
        {/* MODAL: TÃ‰RMINOS & CONDICIONES */}
        {/* ============================================= */}
        {termsModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Términos y Condiciones de Servicio
                </h2>
                <button
                  onClick={() => setTermsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 text-gray-700 dark:text-gray-300">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Última actualización: 24 de enero de 2026
                </div>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    1. Aceptación de Términos
                  </h3>
                  <p>
                    Al acceder y utilizar la plataforma KIKAI ("el Servicio"), usted acepta quedar vinculado por estos 
                    Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no podrá acceder 
                    al Servicio. Este es un acuerdo legal vinculante entre usted (el "Usuario" o "Cliente") y KIKAI 
                    ("nosotros", "nuestro" o "la Empresa").
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    2. Descripción del Servicio
                  </h3>
                  <p className="mb-2">
                    KIKAI es una plataforma SaaS especializada en soporte técnico para equipos pesados Caterpillar, que proporciona:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Diagnóstico técnico asistido por inteligencia artificial</li>
                    <li>Acceso a documentación técnica y manuales de servicio</li>
                    <li>Sistema de tickets para consultas especializadas</li>
                    <li>Búsqueda de números de parte y especificaciones técnicas</li>
                    <li>Chat en tiempo real con asistencia técnica especializada</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    3. Registro y Cuentas de Usuario
                  </h3>
                  <p className="mb-2">
                    Para utilizar el Servicio, debe:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Proporcionar información precisa, actual y completa durante el proceso de registro</li>
                    <li>Mantener la seguridad de su contraseña y aceptar toda responsabilidad por actividades bajo su cuenta</li>
                    <li>Notificarnos inmediatamente sobre cualquier uso no autorizado de su cuenta</li>
                    <li>Tener al menos 18 años o contar con autorización de su organización</li>
                  </ul>
                  <p className="mt-2">
                    Nos reservamos el derecho de suspender o cancelar cuentas que violen estos términos.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    4. Planes de Suscripción y Facturación
                  </h3>
                  <p className="mb-2">
                    KIKAI ofrece múltiples niveles de servicio (Básico, Profesional, Premium) con diferentes características 
                    y límites de uso:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Los pagos se procesan mensual o anualmente según el plan seleccionado</li>
                    <li>Las tarifas se cobran por adelantado y no son reembolsables salvo lo establecido por ley</li>
                    <li>Nos reservamos el derecho de modificar precios con 30 días de aviso previo</li>
                    <li>El Cliente es responsable de mantener información de pago válida y actualizada</li>
                    <li>El exceso de límites de uso puede resultar en cargos adicionales según tarifa vigente</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    5. Uso Aceptable del Servicio
                  </h3>
                  <p className="mb-2">
                    El Usuario se compromete a NO:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Utilizar el Servicio para fines ilegales o no autorizados</li>
                    <li>Intentar obtener acceso no autorizado a sistemas o redes</li>
                    <li>Distribuir malware, virus o código malicioso</li>
                    <li>Realizar ingeniería inversa, descompilar o desensamblar el software</li>
                    <li>Revender, sublicenciar o transferir el acceso sin autorización escrita</li>
                    <li>Sobrecargar intencionalmente la infraestructura del servicio</li>
                    <li>Extraer datos mediante scraping, crawling o métodos automatizados no autorizados</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    6. Propiedad Intelectual
                  </h3>
                  <p>
                    Todo el contenido, características y funcionalidades del Servicio, incluyendo pero no limitado a 
                    software, interfaces, diseños, logos, documentación y algoritmos de IA, son propiedad exclusiva de 
                    KIKAI y están protegidos por leyes de propiedad intelectual. La documentación técnica de Caterpillar 
                    permanece como propiedad de Caterpillar Inc. y se proporciona bajo licencia.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    7. Limitación de Responsabilidad
                  </h3>
                  <p className="mb-2">
                    El Servicio se proporciona "tal cual" sin garantías de ningún tipo. KIKAI no será responsable por:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Daños indirectos, incidentales, especiales o consecuentes</li>
                    <li>Pérdida de beneficios, datos o tiempo de inactividad de equipos</li>
                    <li>Decisiones técnicas tomadas basándose en la información proporcionada</li>
                    <li>Interrupciones del servicio por mantenimiento, actualizaciones o causas fuera de nuestro control</li>
                  </ul>
                  <p className="mt-2">
                    Las recomendaciones técnicas son orientativas. El Usuario es responsable de validar toda información 
                    crítica antes de implementar reparaciones o procedimientos.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    8. Terminación del Servicio
                  </h3>
                  <p>
                    Cualquier parte puede terminar la relación contractual en cualquier momento. Nos reservamos el derecho 
                    de suspender o terminar el acceso inmediatamente en caso de violación de estos términos. Al terminar 
                    la relación, cesarán todos los derechos de acceso y uso del Servicio.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    9. Modificaciones
                  </h3>
                  <p>
                    Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios significativos 
                    serán notificados con al menos 15 días de anticipación. El uso continuado del Servicio después de las 
                    modificaciones constituye aceptación de los nuevos términos.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    10. Ley Aplicable y Jurisdicción
                  </h3>
                  <p>
                    Estos términos se regirán e interpretarán de acuerdo con las leyes de Chile. Cualquier disputa se 
                    resolverá en los tribunales competentes de Santiago, Chile.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    11. Contacto
                  </h3>
                  <p>
                    Para preguntas sobre estos términos, contáctenos en: <br />
                    <span className="font-medium">Email:</span> support@ingeai7.com <br />
                    <span className="font-medium">Dirección:</span> Santiago, Chile
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setTermsModalOpen(false)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================= */}
        {/* MODAL: POLÃ�TICA DE PRIVACIDAD */}
        {/* ============================================= */}
        {privacyModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Política de Privacidad
                </h2>
                <button
                  onClick={() => setPrivacyModalOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 text-gray-700 dark:text-gray-300">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Última actualización: 24 de enero de 2026
                </div>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    1. Introducción
                  </h3>
                  <p>
                    KIKAI ("nosotros", "nuestro" o "la Empresa") se compromete a proteger la privacidad de nuestros usuarios. 
                    Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos y protegemos su información 
                    personal cuando utiliza nuestra plataforma de soporte técnico especializado.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    2. Información que Recopilamos
                  </h3>
                  
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">
                    2.1 Información de Cuenta
                  </h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Nombre completo y datos de contacto</li>
                    <li>Dirección de correo electrónico</li>
                    <li>Información de la empresa u organización</li>
                    <li>Rol profesional y nivel de acceso</li>
                  </ul>

                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">
                    2.2 Información de Uso del Servicio
                  </h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Consultas técnicas y tickets de soporte generados</li>
                    <li>Archivos cargados (manuales, imágenes de equipos, documentos técnicos)</li>
                    <li>Historial de búsquedas y números de parte consultados</li>
                    <li>Modelos de equipos y configuraciones técnicas consultadas</li>
                    <li>Interacciones con el sistema de IA y chat técnico</li>
                  </ul>

                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">
                    2.3 Información Técnica y de Sistema
                  </h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Dirección IP y datos de geolocalización</li>
                    <li>Tipo de navegador, sistema operativo y dispositivo</li>
                    <li>Cookies y tecnologías de seguimiento</li>
                    <li>Logs de acceso y actividad del sistema</li>
                  </ul>

                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">
                    2.4 Información de Facturación
                  </h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Información de pago (procesada a través de proveedores certificados PCI-DSS)</li>
                    <li>Historial de transacciones y facturación</li>
                    <li>Datos fiscales y de facturación (RUT/NIT/Tax ID)</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    3. Cómo Utilizamos su Información
                  </h3>
                  <p className="mb-2">
                    Utilizamos la información recopilada para:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Proporcionar, mantener y mejorar nuestros servicios de soporte técnico</li>
                    <li>Procesar consultas técnicas y generar diagnósticos mediante IA</li>
                    <li>Gestionar su cuenta, suscripción y facturación</li>
                    <li>Enviar notificaciones sobre su cuenta, tickets y actualizaciones del servicio</li>
                    <li>Personalizar su experiencia y mejorar la precisión de las respuestas técnicas</li>
                    <li>Realizar análisis estadísticos y mejorar nuestros algoritmos de IA</li>
                    <li>Detectar, prevenir y resolver problemas técnicos y de seguridad</li>
                    <li>Cumplir con obligaciones legales y regulatorias</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    4. Compartir Información con Terceros
                  </h3>
                  <p className="mb-2">
                    Podemos compartir su información con:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Proveedores de servicios:</strong> Hosting, procesamiento de pagos, análisis de datos</li>
                    <li><strong>Caterpillar Inc.:</strong> Para acceso a documentación técnica oficial (bajo acuerdos de confidencialidad)</li>
                    <li><strong>Autoridades legales:</strong> Cuando sea requerido por ley o para proteger derechos legales</li>
                  </ul>
                  <p className="mt-2">
                    <strong>NO vendemos ni alquilamos su información personal a terceros.</strong>
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    5. Seguridad de Datos
                  </h3>
                  <p className="mb-2">
                    Implementamos medidas de seguridad técnicas y organizativas para proteger su información:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Cifrado TLS/SSL para todas las transmisiones de datos</li>
                    <li>Cifrado en reposo para datos sensibles en base de datos</li>
                    <li>Autenticación multi-factor disponible para cuentas</li>
                    <li>Controles de acceso basados en roles (RBAC)</li>
                    <li>Auditorías de seguridad regulares y pruebas de penetración</li>
                    <li>Backups automáticos y plan de recuperación ante desastres</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    6. Retención de Datos
                  </h3>
                  <p>
                    Retenemos su información personal mientras su cuenta esté activa o según sea necesario para proporcionarle 
                    servicios. Los datos de tickets y consultas técnicas se mantienen por 5 años para fines de soporte y análisis. 
                    Puede solicitar la eliminación de sus datos en cualquier momento, sujeto a obligaciones legales de retención.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    7. Sus Derechos de Privacidad
                  </h3>
                  <p className="mb-2">
                    Usted tiene derecho a:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Acceso:</strong> Solicitar una copia de su información personal</li>
                    <li><strong>Rectificación:</strong> Corregir información inexacta o incompleta</li>
                    <li><strong>Eliminación:</strong> Solicitar la eliminación de sus datos (derecho al olvido)</li>
                    <li><strong>Portabilidad:</strong> Recibir sus datos en formato estructurado y legible</li>
                    <li><strong>Restricción:</strong> Limitar el procesamiento de su información</li>
                    <li><strong>Oposición:</strong> Oponerse al procesamiento de sus datos para ciertos fines</li>
                    <li><strong>Revocación:</strong> Retirar el consentimiento en cualquier momento</li>
                  </ul>
                  <p className="mt-2">
                    Para ejercer estos derechos, contacte a: <span className="font-medium">support@ingeai7.com</span>
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    8. Cookies y Tecnologías Similares
                  </h3>
                  <p>
                    Utilizamos cookies esenciales para el funcionamiento del servicio, cookies de análisis para mejorar la 
                    experiencia del usuario, y cookies de preferencias para recordar configuraciones. Puede gestionar las 
                    preferencias de cookies desde la configuración de su navegador.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    9. Transferencias Internacionales
                  </h3>
                  <p>
                    Sus datos pueden ser transferidos y procesados en servidores ubicados fuera de su país de residencia. 
                    Implementamos salvaguardas adecuadas, incluyendo cláusulas contractuales estándar, para garantizar un 
                    nivel de protección equivalente al requerido en su jurisdicción.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    10. Menores de Edad
                  </h3>
                  <p>
                    Nuestro servicio no está dirigido a menores de 18 años. No recopilamos intencionalmente información 
                    de menores. Si detectamos que un menor ha proporcionado información personal, la eliminaremos de 
                    nuestros sistemas.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    11. Cambios a esta Política
                  </h3>
                  <p>
                    Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cambios 
                    significativos mediante correo electrónico o un aviso destacado en el servicio. La versión actualizada 
                    entrará en vigor inmediatamente después de su publicación.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    12. Contacto
                  </h3>
                  <p>
                    Para preguntas o inquietudes sobre esta política, contáctenos: <br />
                    <span className="font-medium">Email:</span> support@ingeai7.com <br />
                    <span className="font-medium">DPO (Oficial de Protección de Datos):</span> support@ingeai7.com <br />
                    <span className="font-medium">Dirección:</span> Santiago, Chile
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setPrivacyModalOpen(false)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}